import * as React from 'react';
import axios from 'axios';
import { Source } from 'react-map-gl';
import centroid from '@turf/centroid';

import { ILoaderProps } from './ILoaderProps';
import { Config } from '../../Config';

interface IProps {
  /** @ignore */
  children?: React.ReactNode;
  /** GeoJSON file name. */
  path: string;
  /** Add clustering? */
  cluster?: boolean;
  /** Is layer currently active? */
  active: boolean;
  /** List of properties to ignore. */
  ignore?: string[]
}

/**
 * Loads a geojson resource (path) from the server. Renders a 
 * Source which may contain Layer children.
 */
const LoadableSource = (props: IProps & ILoaderProps) => {
  const [data, setData] = React.useState<GeoJSON.FeatureCollection>(null);

  React.useEffect(() => {
    if (props.active) loadData();
  }, [props.active]);

  const loadData = () => {
    window.setTimeout(() => {
      props.onLoading();
      axios.get(props.path)
        .then(res => {
          let geojson = res.data as GeoJSON.FeatureCollection;

          // Filter GeoJSON for ignored properties:
          if (props.ignore?.length) {
            geojson.features.forEach(f => {
              f.properties = Object.keys(f.properties)
                .filter(key => !props.ignore.includes(key))
                .reduce((obj, key) => {
                  (obj as any)[key] = f.properties[key];
                  return obj;
                }, {});
            });
          }

          // If clustering is enabled, ensure geometries are points
          if (props.cluster) {
            const allPolygons = geojson.features.every(
              f =>
                f.geometry.type === 'Polygon' ||
                f.geometry.type === 'MultiPolygon'
            );

            const allPoints = geojson.features.every(
              f => f.geometry.type === 'Point'
            );

            if (!allPoints && allPolygons) {
              // Convert polygons → centroid points (keep already filtered props)
              geojson = {
                type: 'FeatureCollection',
                features: geojson.features.map(f => {
                  const c = centroid(f);
                  c.properties = { ...f.properties }; // copy filtered props
                  return c;
                }),
              };
            }
          }

          setData(geojson);
        })
        .catch(err => console.error('Error loading GeoJSON:', err))
        .finally(() => props.onLoaded());
    }, Math.random() * Config.UI.load_delay_random + Config.UI.load_delay_base);
  }

  return (
    // generateId creates an ID for each feature, since the source data
    // does not have any IDs. This is required for hovering.
    <Source
      key={`${props.path}_${props.cluster ? 'cluster' : 'poly'}`}
      id={`${props.path}_${props.cluster ? 'cluster' : 'poly'}`}
      cluster={props.cluster ? true : false}
      clusterRadius={75}
      generateId
      type="geojson"
      data={data}
    >
      {props.children}
    </Source>
  );
}

export { LoadableSource }
